import { Link } from "react-router-dom";
import { Linkedin, Mail, ArrowUpRight } from "lucide-react";
import { NAV_LINKS } from "@/data/content";
import { LogoMark, Wordmark } from "@/components/Logo";

export default function Footer() {
    const year = new Date().getFullYear();
    return (
        <footer className="border-t border-border bg-background">
            <div className="mx-auto max-w-7xl px-6 pb-14 pt-24 lg:px-10 lg:pt-32">
                <div className="grid gap-12 lg:grid-cols-12">
                    <div className="lg:col-span-6">
                        <div className="flex items-center gap-3">
                            <LogoMark className="h-12 w-12" />
                            <Wordmark className="text-3xl md:text-4xl" />
                        </div>
                        <p className="mt-6 font-display text-2xl font-semibold leading-snug tracking-tight md:text-3xl">
                            Automate the repetitive.{" "}
                            <span className="text-navy">Empower the people.</span>
                        </p>
                        <p className="mt-6 max-w-md text-base leading-relaxed text-muted-foreground">
                            We identify repetitive operational work in shipping
                            and logistics, and automate it using AI, so your
                            people can focus on the decisions that matter.
                        </p>
                        <div className="mt-8">
                            <Link
                                to="/contact"
                                data-testid="footer-cta"
                                className="group inline-flex items-center gap-2 text-lg font-semibold text-foreground"
                            >
                                Book a free automation assessment
                                <ArrowUpRight
                                    className="h-5 w-5 text-navy transition-transform duration-300 ease-premium group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                                    strokeWidth={2}
                                />
                            </Link>
                        </div>
                    </div>

                    <div className="lg:col-span-3 lg:col-start-8">
                        <h4 className="font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground">
                            Navigation
                        </h4>
                        <ul className="mt-5 space-y-3">
                            <li>
                                <Link
                                    to="/"
                                    data-testid="footer-link-home"
                                    className="text-muted-foreground transition-colors duration-300 hover:text-foreground"
                                >
                                    Home
                                </Link>
                            </li>
                            {NAV_LINKS.map((l) => (
                                <li key={l.to}>
                                    <Link
                                        to={l.to}
                                        data-testid={`footer-link-${l.to.slice(1)}`}
                                        className="text-muted-foreground transition-colors duration-300 hover:text-foreground"
                                    >
                                        {l.label}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="lg:col-span-2">
                        <h4 className="font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground">
                            Connect
                        </h4>
                        <ul className="mt-5 space-y-3">
                            <li>
                                <a
                                    href="https://www.linkedin.com"
                                    target="_blank"
                                    rel="noreferrer"
                                    data-testid="footer-linkedin"
                                    className="inline-flex items-center gap-2 text-muted-foreground transition-colors duration-300 hover:text-foreground"
                                >
                                    <Linkedin className="h-4 w-4" strokeWidth={1.5} />
                                    LinkedIn
                                </a>
                            </li>
                            <li>
                                <a
                                    href="mailto:support@harbrflow.com"
                                    data-testid="footer-email"
                                    className="inline-flex items-center gap-2 text-muted-foreground transition-colors duration-300 hover:text-foreground"
                                >
                                    <Mail className="h-4 w-4" strokeWidth={1.5} />
                                    Email
                                </a>
                            </li>
                        </ul>
                    </div>
                </div>

                <div className="mt-20 flex flex-col items-start justify-between gap-4 border-t border-border pt-8 font-mono text-xs text-muted-foreground sm:flex-row sm:items-center">
                    <span>© {year} harbrflow. All rights reserved.</span>
                    <div className="flex items-center gap-6">
                        <Link
                            to="/privacy"
                            data-testid="footer-privacy"
                            className="transition-colors duration-300 hover:text-foreground"
                        >
                            Privacy Policy
                        </Link>
                        <span className="uppercase tracking-[0.2em]">
                            AI Automation · Shipping &amp; Logistics
                        </span>
                    </div>
                </div>
            </div>
        </footer>
    );
}
